import { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { useDispatch } from "react-redux";
import ReactCountryFlag from "react-country-flag";
import { getData } from "country-list";
import {
  fetchStates,
  fetchCities,
} from "~/components/entities/address/addressesSlice";
import { DirectorFormData, CountryOption } from "../../types/director";
import { AppDispatch } from "~/store";
import PermissionGate from "~/components/PermissionGate";
import CustomeButton from "~/components/Button";

const countries: CountryOption[] = getData()
  .map((country) => ({
    name: country.name,
    code: country.code,
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

interface AddressFormProps {
  isEditMode?: boolean;
  onDiscard?: () => void;
  onSave?: () => void;
  isSaving?: boolean;
  isEditing?: boolean;
  setIsEditing?: (value: boolean) => void;
  onViewHistory?: () => void;
  showViewHistory?: boolean;
  entityId?: string;
}

const AddressForm = ({
  isEditMode = false,
  onDiscard,
  onSave,
  isSaving = false,
  isEditing: isEditingProp,
  setIsEditing: setIsEditingProp,
  onViewHistory,
  showViewHistory = false,
  entityId,
}: AddressFormProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const [isEditingLocal, setIsEditingLocal] = useState(!isEditMode);

  // Use props if provided, otherwise fall back to local state
  const isEditing =
    isEditingProp !== undefined ? isEditingProp : isEditingLocal;
  const setIsEditing = setIsEditingProp || setIsEditingLocal;
  const {
    control,
    watch,
    setValue,
    getValues,
    reset,
    formState: { errors, isValid, isDirty },
  } = useFormContext<DirectorFormData>();

  const isDisabled = isEditMode && !isEditing;
  const canSave = isDirty && isValid;

  const [stateOptions, setStateOptions] = useState<CountryOption[]>([]);
  const [cityOptions, setCityOptions] = useState<CountryOption[]>([]);
  const [isStateLoading, setIsStateLoading] = useState(false);
  const [isCityLoading, setIsCityLoading] = useState(false);
  const [previousCountryCode, setPreviousCountryCode] = useState<string | null>(
    null,
  );
  const [previousStateCode, setPreviousStateCode] = useState<string | null>(
    null,
  );

  const selectedCountry = watch("address_country");
  const selectedState = watch("address_state");

  const handleEnableEdit = () => {
    setIsEditing(true);
  };

  const handleDiscard = () => {
    reset();
    setIsEditing(false);
    onDiscard?.();
  };

  const handleSave = () => {
    onSave?.();
  };

  useEffect(() => {
    if (selectedCountry?.code) {
      const isCountryChange =
        previousCountryCode !== null &&
        previousCountryCode !== selectedCountry.code;

      setIsStateLoading(true);
      dispatch(fetchStates(selectedCountry.code))
        .then((response: any) => {
          const options =
            response.payload?.map((state: any) => ({
              name: state.label,
              code: state.value,
            })) || [];
          setStateOptions(options);
          setIsStateLoading(false);

          // Get current state value inside callback to avoid stale closure
          const currentState = getValues("address_state");

          // If there's a selected state, update it with the correct option from the list
          if (currentState?.code && !isCountryChange) {
            const matchingState = options.find(
              (opt: CountryOption) => opt.code === currentState.code,
            );
            if (matchingState) {
              setValue("address_state", matchingState);
            }
          }
        })
        .catch(() => {
          setStateOptions([]);
          setIsStateLoading(false);
        });

      // Only reset state and city when user changes country (not on initial load)
      if (isCountryChange) {
        setValue("address_state", null);
        setValue("address_city", null);
        setCityOptions([]);
      }

      setPreviousCountryCode(selectedCountry.code);
    }
  }, [selectedCountry?.code]);

  useEffect(() => {
    if (selectedCountry?.code && selectedState?.code) {
      const isStateChange =
        previousStateCode !== null && previousStateCode !== selectedState.code;

      setIsCityLoading(true);
      dispatch(fetchCities([selectedCountry.code, selectedState.code]))
        .then((response: any) => {
          const options =
            response.payload?.map((city: any) => ({
              name: city.label,
              code: city.value,
            })) || [];
          setCityOptions(options);
          setIsCityLoading(false);

          // Get current city value inside callback to avoid stale closure
          const currentCity = getValues("address_city");

          // If there's a selected city, update it with the correct option from the list
          if (currentCity?.code && !isStateChange) {
            const matchingCity = options.find(
              (opt: CountryOption) => opt.code === currentCity.code,
            );
            if (matchingCity) {
              setValue("address_city", matchingCity);
            }
          }
        })
        .catch(() => {
          setCityOptions([]);
          setIsCityLoading(false);
        });

      // Only reset city when user changes state (not on initial load)
      if (isStateChange) {
        setValue("address_city", null);
      }

      setPreviousStateCode(selectedState.code);
    }
  }, [selectedState?.code]);

  const countryItemTemplate = (option: CountryOption) => (
    <div className="flex align-items-center">
      <ReactCountryFlag
        countryCode={option.code}
        svg
        className="flag-svg-icon"
      />
      <span>{option.name}</span>
    </div>
  );

  return (
    <div className="director-form flex flex-column gap-2">
      {/* Header */}
      <div className="flex justify-content-between align-items-start">
        <div className="flex flex-column gap-2">
          <p className="form-title m-0">Address</p>
          <div className="form-description m-0">
            Please provide the registered address for this director
          </div>
        </div>
        <PermissionGate
          resource="director"
          action="modify"
          resourceAttr={{ entity_id: entityId }}
        >
          {isEditMode && (
            <div className="flex gap-2">
              {isEditing ? (
                <>
                  <Button
                    label="Discard"
                    className="director-discard-btn"
                    onClick={handleDiscard}
                  />
                  <Button
                    label="Save Changes"
                    className={`director-save-btn ${!canSave ? "disabled" : ""}`}
                    onClick={handleSave}
                    icon={isSaving ? "pi pi-spin pi-spinner" : undefined}
                    disabled={!canSave || isSaving}
                  />
                </>
              ) : (
                <>
                  {showViewHistory && (
                    <CustomeButton
                      label="View History"
                      variant="outlined"
                      className="mr-2"
                      icon="pi pi-history"
                      iconPos="right"
                      onClick={onViewHistory}
                    />
                  )}
                  <Button
                    label="Edit"
                    className="director-edit-btn"
                    icon="pi pi-pencil"
                    iconPos="right"
                    onClick={handleEnableEdit}
                  />
                </>
              )}
            </div>
          )}
        </PermissionGate>
      </div>

      {/* Registered Address Section */}
      <div className="flex flex-column gap-2 mt-3">
        <p className="form-section-title m-0">Registered Address</p>
      </div>

      <div className="row flex gap-3">
        <div className="flex flex-column col-6 pl-0 pr-0 gap-2">
          <label htmlFor="address_country">Country*</label>
          <Controller
            name="address_country"
            control={control}
            rules={{ required: "Country is required" }}
            render={({ field }) => (
              <Dropdown
                {...field}
                options={countries}
                optionLabel="name"
                placeholder="Select country"
                disabled={isDisabled}
                className={`w-full officer-dropdown-field ${errors.address_country ? "error" : ""}`}
                itemTemplate={countryItemTemplate}
                appendTo="self"
              />
            )}
          />
          {errors.address_country && (
            <small className="error-message">
              {errors.address_country.message}
            </small>
          )}
        </div>
      </div>

      <div className="row flex gap-3">
        <div className="flex flex-column col-12 pl-0 pr-0 gap-2">
          <label htmlFor="address_line_1">Address Line 1*</label>
          <Controller
            name="address_line_1"
            control={control}
            rules={{ required: "Address Line 1 is required" }}
            render={({ field }) => (
              <InputText
                {...field}
                placeholder="Enter street address"
                disabled={isDisabled}
                className={`input-field appearance-none outline-none w-full ${errors.address_line_1 ? "error" : ""}`}
              />
            )}
          />
          {errors.address_line_1 && (
            <small className="error-message">
              {errors.address_line_1.message}
            </small>
          )}
        </div>
      </div>

      <div className="row flex gap-3">
        <div className="flex flex-column col-12 pl-0 pr-0 gap-2">
          <label htmlFor="address_line_2">Address Line 2</label>
          <Controller
            name="address_line_2"
            control={control}
            render={({ field }) => (
              <InputText
                {...field}
                placeholder="Apartment, suite, or building number (optional)"
                disabled={isDisabled}
                className="input-field appearance-none outline-none w-full"
              />
            )}
          />
        </div>
      </div>

      <div className="row flex gap-3">
        <div className="flex flex-column col-6 pl-0 pr-0 gap-2">
          <label htmlFor="address_state">State/Province</label>
          <Controller
            name="address_state"
            control={control}
            render={({ field }) => (
              <Dropdown
                {...field}
                options={stateOptions}
                optionLabel="name"
                placeholder="Select State or Province"
                className="w-full officer-dropdown-field"
                loading={isStateLoading}
                disabled={isDisabled || !selectedCountry}
                appendTo="self"
                onChange={(e) => field.onChange(e.value)}
                value={field.value}
              />
            )}
          />
        </div>

        <div className="flex flex-column col-6 pl-0 pr-0 gap-2">
          <label htmlFor="address_city">City</label>
          <Controller
            name="address_city"
            control={control}
            render={({ field }) => (
              <Dropdown
                {...field}
                options={cityOptions}
                optionLabel="name"
                placeholder="Select city"
                className="w-full officer-dropdown-field"
                loading={isCityLoading}
                disabled={isDisabled || !selectedState}
                appendTo="self"
                onChange={(e) => field.onChange(e.value)}
                value={field.value}
              />
            )}
          />
        </div>
      </div>

      <div className="row flex gap-3">
        <div className="flex flex-column col-6 pl-0 pr-0 gap-2">
          <label htmlFor="address_postal_code">Postal Code / Zip Code*</label>
          <Controller
            name="address_postal_code"
            control={control}
            rules={{ required: "Postal Code is required" }}
            render={({ field }) => (
              <InputText
                {...field}
                placeholder="Postal code or ZIP code"
                disabled={isDisabled}
                className={`input-field appearance-none outline-none w-full ${errors.address_postal_code ? "error" : ""}`}
              />
            )}
          />
          {errors.address_postal_code && (
            <small className="error-message">
              {errors.address_postal_code.message}
            </small>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddressForm;
